import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Stethoscope, Plus, AlertTriangle, Loader2, Infinity } from 'lucide-react';
import { toast } from 'sonner';

const AtestadosPage = () => {
  const { api, user } = useAuth();
  const [atestados, setAtestados] = useState([]);
  const [empregados, setEmpregados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    empregado_id: '',
    tipo_atestado: 'medico',
    tipo_atestado_outros: '',
    tipo_orgao_emissor: 'clinica',
    tipo_orgao_emissor_outro: '',
    cid: '',
    medico: '',
    crm: '',
    data_inicio: '',
    data_fim: '',
    dias_afastamento: 1,
    indeterminado: false,
    observacoes: '',
    empresa_id: user?.empresa_id || ''
  });

  // Opções de tipo de atestado
  const tiposAtestado = [
    { value: 'medico', label: 'Atestado Médico' },
    { value: 'odontologico', label: 'Atestado Odontológico' },
    { value: 'psicologico', label: 'Atestado Psicológico' },
    { value: 'comparecimento', label: 'Comparecimento' },
    { value: 'outros', label: 'Outros (especificar)' }
  ];

  // Opções de órgão emissor
  const tiposOrgaoEmissor = [
    { value: 'clinica', label: 'Clínica' },
    { value: 'hospital', label: 'Hospital' },
    { value: 'servico_municipal', label: 'Serviço Municipal' },
    { value: 'outro', label: 'Outro (especificar)' }
  ];

  useEffect(() => {
    fetchAtestados();
    fetchEmpregados();
  }, []);

  const fetchAtestados = async () => {
    try {
      const response = await api.get('/atestados');
      setAtestados(response.data);
    } catch (error) {
      console.error('Error fetching atestados:', error);
      toast.error('Erro ao carregar atestados');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpregados = async () => {
    try {
      const response = await api.get('/empregados');
      setEmpregados(response.data.filter(e => e.ativo));
    } catch (error) {
      console.error('Error fetching empregados:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/atestados', formData);
      toast.success('Atestado registrado!');
      setDialogOpen(false);
      fetchAtestados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar atestado');
    }
  };

  const getEmpregadoNome = (id) => {
    const emp = empregados.find(e => e.id === id);
    return emp ? emp.nome : 'N/A';
  };

  const getStatusBadge = (status) => {
    const config = {
      pendente: { label: 'Pendente', className: 'bg-warning' },
      aprovada: { label: 'Aprovado', className: 'bg-accent' },
      rejeitada: { label: 'Rejeitado', className: 'bg-destructive' }
    };
    const c = config[status] || config.pendente;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const atestadosNtep = atestados.filter(a => a.coincide_ntep);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="atestados-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Atestados Médicos</h2>
          <p className="text-muted-foreground">Gestão de afastamentos médicos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" data-testid="novo-atestado-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Atestado
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Registrar Atestado</DialogTitle>
              <DialogDescription>Registre um novo atestado médico</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="empregado">Empregado *</Label>
                  <Select
                    value={formData.empregado_id}
                    onValueChange={(value) => setFormData({...formData, empregado_id: value})}
                  >
                    <SelectTrigger data-testid="select-empregado">
                      <SelectValue placeholder="Selecione o empregado" />
                    </SelectTrigger>
                    <SelectContent>
                      {empregados.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.matricula} - {emp.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tipo de Atestado */}
                <div className="space-y-2">
                  <Label htmlFor="tipo_atestado">Tipo de Atestado *</Label>
                  <Select
                    value={formData.tipo_atestado}
                    onValueChange={(value) => setFormData({...formData, tipo_atestado: value, tipo_atestado_outros: value !== 'outros' ? '' : formData.tipo_atestado_outros})}
                  >
                    <SelectTrigger data-testid="select-tipo-atestado">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposAtestado.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Campo "Outros" para tipo de atestado */}
                {formData.tipo_atestado === 'outros' && (
                  <div className="space-y-2">
                    <Label htmlFor="tipo_atestado_outros">Especificar Tipo *</Label>
                    <Input
                      id="tipo_atestado_outros"
                      value={formData.tipo_atestado_outros}
                      onChange={(e) => setFormData({...formData, tipo_atestado_outros: e.target.value})}
                      placeholder="Digite o tipo do atestado"
                      required
                      data-testid="input-tipo-atestado-outros"
                    />
                  </div>
                )}

                {/* Órgão Emissor */}
                <div className="space-y-2">
                  <Label htmlFor="tipo_orgao_emissor">Órgão Emissor *</Label>
                  <Select
                    value={formData.tipo_orgao_emissor}
                    onValueChange={(value) => setFormData({...formData, tipo_orgao_emissor: value, tipo_orgao_emissor_outro: value !== 'outro' ? '' : formData.tipo_orgao_emissor_outro})}
                  >
                    <SelectTrigger data-testid="select-orgao-emissor">
                      <SelectValue placeholder="Selecione o órgão" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposOrgaoEmissor.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Campo "Outro" para órgão emissor */}
                {formData.tipo_orgao_emissor === 'outro' && (
                  <div className="space-y-2">
                    <Label htmlFor="tipo_orgao_emissor_outro">Especificar Órgão *</Label>
                    <Input
                      id="tipo_orgao_emissor_outro"
                      value={formData.tipo_orgao_emissor_outro}
                      onChange={(e) => setFormData({...formData, tipo_orgao_emissor_outro: e.target.value})}
                      placeholder="Digite o nome do órgão"
                      required
                      data-testid="input-orgao-emissor-outro"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="cid">CID</Label>
                  <Input
                    id="cid"
                    value={formData.cid}
                    onChange={(e) => setFormData({...formData, cid: e.target.value.toUpperCase()})}
                    placeholder="Ex: M54"
                    data-testid="input-cid"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dias">Dias de Afastamento {!formData.indeterminado && '*'}</Label>
                  <Input
                    id="dias"
                    type="number"
                    min="1"
                    value={formData.indeterminado ? '' : formData.dias_afastamento}
                    onChange={(e) => setFormData({...formData, dias_afastamento: parseInt(e.target.value) || 1})}
                    required={!formData.indeterminado}
                    disabled={formData.indeterminado}
                    placeholder={formData.indeterminado ? 'Indeterminado' : ''}
                    data-testid="input-dias"
                  />
                </div>

                {/* Checkbox Indeterminado */}
                <div className="flex items-center space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Checkbox
                    id="indeterminado"
                    checked={formData.indeterminado}
                    onCheckedChange={(checked) => setFormData({
                      ...formData, 
                      indeterminado: checked,
                      dias_afastamento: checked ? 999 : 1,
                      data_fim: checked ? '' : formData.data_fim
                    })}
                    data-testid="checkbox-indeterminado"
                  />
                  <Label htmlFor="indeterminado" className="text-sm text-amber-800 cursor-pointer flex items-center gap-2">
                    <Infinity className="w-4 h-4" />
                    Afastamento Indeterminado (sem prazo definido)
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data_inicio">Data Início *</Label>
                  <Input
                    id="data_inicio"
                    type="date"
                    value={formData.data_inicio}
                    onChange={(e) => setFormData({...formData, data_inicio: e.target.value})}
                    required
                    data-testid="input-data-inicio"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_fim">Data Fim {!formData.indeterminado && '*'}</Label>
                  <Input
                    id="data_fim"
                    type="date"
                    value={formData.data_fim}
                    onChange={(e) => setFormData({...formData, data_fim: e.target.value})}
                    required={!formData.indeterminado}
                    disabled={formData.indeterminado}
                    placeholder={formData.indeterminado ? 'Indeterminado' : ''}
                    data-testid="input-data-fim"
                  />
                  {formData.indeterminado && (
                    <p className="text-xs text-amber-600">Data fim não aplicável para afastamento indeterminado</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="medico">Médico</Label>
                  <Input
                    id="medico"
                    value={formData.medico}
                    onChange={(e) => setFormData({...formData, medico: e.target.value})}
                    placeholder="Nome do médico (opcional)"
                    data-testid="input-medico"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crm">CRM</Label>
                  <Input
                    id="crm"
                    value={formData.crm}
                    onChange={(e) => setFormData({...formData, crm: e.target.value})}
                    placeholder="CRM/UF (opcional)"
                    data-testid="input-crm"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="observacoes">Observações</Label>
                  <Textarea
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                    placeholder="Observações adicionais..."
                    rows={2}
                    data-testid="input-observacoes"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="submit-atestado-btn">
                  Registrar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {atestadosNtep.length > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Atenção: {atestadosNtep.length} atestado(s) com coincidência NTEP
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Os seguintes atestados apresentam coincidência entre CID e CNAE da empresa, indicando possível nexo técnico epidemiológico.
            </p>
            <div className="space-y-2">
              {atestadosNtep.map((atestado) => (
                <div key={atestado.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-destructive/20">
                  <div>
                    <p className="font-medium">{getEmpregadoNome(atestado.empregado_id)}</p>
                    <p className="text-sm text-muted-foreground">
                      CID: {atestado.cid || 'N/A'} | {atestado.indeterminado ? 'Indeterminado' : `${atestado.dias_afastamento} dias`}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" className="text-destructive" data-testid={`ver-ntep-${atestado.id}`}>
                    Ver Análise NTEP
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-['Outfit']">Todos os Atestados</CardTitle>
        </CardHeader>
        <CardContent>
          {atestados.length === 0 ? (
            <div className="text-center py-12">
              <Stethoscope className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhum atestado registrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empregado</TableHead>
                    <TableHead>CID</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Dias</TableHead>
                    <TableHead>Médico</TableHead>
                    <TableHead>NTEP</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {atestados.map((atestado) => (
                    <TableRow key={atestado.id} data-testid={`atestado-row-${atestado.id}`}>
                      <TableCell className="font-medium">{getEmpregadoNome(atestado.empregado_id)}</TableCell>
                      <TableCell>
                        {atestado.cid ? (
                          <Badge variant="outline" className="font-mono">{atestado.cid}</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(atestado.data_inicio).toLocaleDateString('pt-BR')}
                        {atestado.indeterminado ? (
                          <span className="text-amber-600 font-medium"> → Indeterminado</span>
                        ) : atestado.data_fim ? (
                          <span> a {new Date(atestado.data_fim).toLocaleDateString('pt-BR')}</span>
                        ) : (
                          <span className="text-slate-400"> → Sem data fim</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {atestado.indeterminado ? (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            <Infinity className="w-3 h-3 mr-1" />
                            Indet.
                          </Badge>
                        ) : (
                          atestado.dias_afastamento
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {atestado.medico}<br/>
                        <span className="text-muted-foreground">{atestado.crm}</span>
                      </TableCell>
                      <TableCell>
                        {atestado.coincide_ntep ? (
                          <Badge className="bg-destructive">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Sim
                          </Badge>
                        ) : (
                          <Badge variant="outline">Não</Badge>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(atestado.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AtestadosPage;
